import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useProducts } from '../context/ProductsContext';
import ProductsSharedSections from '../components/products/ProductsSharedSections';

const ORANGE = '#FF6B00';
const DARK = '#1A1A1A';
const MUTED = '#555';
const SOFT = '#F8F9FA';

function Heading({ title, subtitle }) {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h3" sx={{ fontWeight: 800, color: DARK, fontSize: { xs: '1.75rem', md: '2.3rem' } }}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography sx={{ mt: 1, color: MUTED, lineHeight: 1.7, fontSize: { xs: '1rem', md: '1.05rem' } }}>
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );
}

export default function Products() {
  const { pageData, loading, error } = useProducts();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
        <CircularProgress sx={{ color: ORANGE }} />
      </Box>
    );
  }

  if (error || !pageData) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error">
          {error || 'Unable to load products. Please try again later.'}
        </Alert>
      </Container>
    );
  }

  const { products, deckingOptions, rackAccessories, settings } = pageData;
  const heroHighlights = [
    { label: 'Racking Systems', value: products.length },
    { label: 'Decking Options', value: deckingOptions.length },
    { label: 'Rack Accessories', value: rackAccessories.length },
  ];

  return (
    <Box sx={{ bgcolor: '#fff', overflowX: 'hidden' }}>
      <Box
        sx={{
          position: 'relative',
          bgcolor: '#fff',
          borderBottom: '1px solid #ededed',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(540px 260px at 10% 15%, rgba(255,107,0,0.12), transparent), linear-gradient(90deg, rgba(17,17,17,0.055) 1px, transparent 1px), linear-gradient(0deg, rgba(17,17,17,0.055) 1px, transparent 1px)',
            backgroundSize: 'auto, 34px 34px, 34px 34px',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 }, py: { xs: 7, md: 10 }, position: 'relative', zIndex: 1 }}>
          <Grid container spacing={{ xs: 3, md: 5 }} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box component={motion.div} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <Typography sx={{ color: ORANGE, fontWeight: 900, letterSpacing: 3, mb: 1, textTransform: 'uppercase', fontSize: '0.78rem' }}>
                  {settings?.heroOverline || 'Product Portfolio'}
                </Typography>
                <Typography sx={{ fontWeight: 900, color: DARK, lineHeight: { xs: 1.08, md: 0.98 }, fontSize: { xs: '2rem', sm: '2.7rem', md: '3.5rem' }, mb: 1.25 }}>
                  {settings?.heroTitle || 'Engineered Storage Products for Every Warehouse Need'}
                </Typography>
                <Typography sx={{ color: MUTED, lineHeight: 1.7, fontSize: { xs: '1rem', md: '1.08rem' }, maxWidth: 760, mb: 3 }}>
                  {settings?.heroSubtitle}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <Button component={RouterLink} to="/contact" variant="contained" sx={{ bgcolor: ORANGE, borderRadius: 0, fontWeight: 800, px: 3.2, '&:hover': { bgcolor: '#e66000' } }}>
                    Discuss Your Layout
                  </Button>
                  <Button variant="outlined" href="#products-systems" sx={{ borderRadius: 0, fontWeight: 800, color: DARK, borderColor: '#cfcfcf', px: 3.2, '&:hover': { borderColor: ORANGE, color: ORANGE } }}>
                    Browse Racking Systems
                  </Button>
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ border: '1px solid #e5e5e5', borderRadius: 0, p: { xs: 2, md: 2.25 }, bgcolor: '#fff', background: 'linear-gradient(180deg, rgba(255,107,0,0.06) 0%, rgba(255,107,0,0) 45%)' }}>
                <Typography sx={{ fontWeight: 900, color: DARK, mb: 1.6, fontSize: '0.96rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  Quick Scope
                </Typography>
                <Grid container spacing={1.2}>
                  {heroHighlights.map((item) => (
                    <Grid item xs={12} sm={4} md={12} key={item.label}>
                      <Box sx={{ border: '1px solid #ececec', borderLeft: `3px solid ${ORANGE}`, px: 1.4, py: 1.05, bgcolor: '#fff', minHeight: 74, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography sx={{ color: '#4d4d4d', fontSize: '0.88rem', fontWeight: 600, pr: 1.5, lineHeight: 1.35 }}>{item.label}</Typography>
                        <Typography sx={{ color: ORANGE, fontWeight: 900, fontSize: '1.45rem', lineHeight: 1 }}>{String(item.value).padStart(2, '0')}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: '#fff' }} id="products-systems">
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Heading title="Racking Systems" subtitle="Select a system to view full details — comparison, decking, and accessories follow on each page." />
          <Grid container spacing={3}>
            {products.map((product) => (
              <Grid item xs={12} md={6} key={product.id}>
                <Paper
                  component={RouterLink}
                  to={`/products/${product.id}`}
                  elevation={0}
                  sx={{ border: '1px solid #e4e4e4', borderRadius: 0, p: 3, height: '100%', display: 'block', textDecoration: 'none', color: 'inherit', transition: 'border-color 0.2s ease, box-shadow 0.2s ease', '&:hover': { borderColor: ORANGE, boxShadow: '0 8px 24px rgba(0,0,0,0.06)' } }}
                >
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={5}>
                      <Box sx={{ border: '1px solid #e4e4e4', bgcolor: SOFT, height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1.5 }}>
                        <Box component="img" src={product.image} alt={product.name} sx={{ maxWidth: '100%', maxHeight: 190, objectFit: 'contain' }} />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={7}>
                      <Typography sx={{ fontWeight: 800, color: DARK, lineHeight: 1.25, mb: 0.75, fontSize: { xs: '1.08rem', md: '1.2rem' } }}>{product.name}</Typography>
                      <Typography sx={{ color: ORANGE, fontWeight: 600, mb: 1, fontSize: '0.95rem' }}>{product.tagline}</Typography>
                      <Typography sx={{ color: MUTED, lineHeight: 1.65, mb: 1.25, fontSize: '0.95rem' }}>{product.description}</Typography>
                      <Stack spacing={0.7}>
                        {product.features.slice(0, 4).map((feature) => (
                          <Stack key={feature} direction="row" spacing={1} alignItems="flex-start">
                            <CheckCircleOutlineIcon sx={{ color: ORANGE, fontSize: 16, mt: 0.2, flexShrink: 0 }} />
                            <Typography sx={{ color: MUTED, lineHeight: 1.45, fontSize: '0.88rem' }}>{feature}</Typography>
                          </Stack>
                        ))}
                      </Stack>
                      <Typography sx={{ color: ORANGE, fontWeight: 800, mt: 2, fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        View system details <ArrowForwardIcon sx={{ fontSize: 16 }} />
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <ProductsSharedSections />
    </Box>
  );
}
